import { useEffect, useState } from 'react';
import CardContainer from 'src/components/CardContainer/CardContainer';
import { clsx } from 'clsx';
import {
  useGetAllConfigurationSettingsQuery,
  useGetProjectByIdQuery,
  useLazyGetConfigurationSettingsPromptQuery,
} from 'src/services';
import { useParams } from 'react-router-dom';
import { useAppSelector } from 'src/store';
import { GoToButton } from 'src/components/ButtonWithIcon/GoToButton';
import { ROUTE_PATHS } from 'src/utils/route-paths';
import { AiConfigurationFields } from 'src/pages/ControlDeskPage/AiConfigurationSection/AiConfigurationFields';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { Skeleton } from 'src/components/ui/skeleton';
import { FullScreenButton } from 'src/components/ButtonWithIcon/FullScreenButton';
import type { ConfigurationSettings } from 'src/services/configurationService';

export const AiConfigurationSection = ({
  fullScreenConfigurationBtnClickHandler,
  isFullScreenConfiguration,
}: {
  fullScreenConfigurationBtnClickHandler: () => void;
  isFullScreenConfiguration: boolean;
}) => {
  const { isInactivePlan } = useAppSelector((s) => s.planSlice);
  const { id } = useParams();
  const [settingsPage, setSettingsPage] = useState(1);
  const [settings, setSettings] = useState<ConfigurationSettings[] | null>(null);
  const [activeSettings, setActiveSettings] = useState<ConfigurationSettings | null>(null);
  const [systemPromptActive, setSystemPromptActive] = useState(false);
  const [inputData, setInputData] = useState({});
  const [systemPromptText, setSystemPromptText] = useState(null);
  const { data: currentProject } = useGetProjectByIdQuery(
    { id: id },
    { refetchOnMountOrArgChange: true },
  );
  const { data: allConfigurationSettings, isLoading: isLoadingAllConfigurationSettings } =
    useGetAllConfigurationSettingsQuery(
      { id, page: settingsPage },
      { skip: !id, refetchOnMountOrArgChange: true },
    );

  const [
    getConfigurationPrompt,
    { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt },
  ] = useLazyGetConfigurationSettingsPromptQuery();

  useEffect(() => {
    if (allConfigurationSettings) {
      if (allConfigurationSettings.page_info.has_previous_page) {
        const settngs = settings || [];
        setSettings([...settngs, ...allConfigurationSettings.items]);
      } else {
        setActiveSettings(null);
        setSettings(allConfigurationSettings.items);
      }

      if (allConfigurationSettings.page_info.has_next_page) {
        setSettingsPage(allConfigurationSettings.page_info.page + 1);
      }
    }
  }, [allConfigurationSettings]);

  useEffect(() => {
    if (settings?.length) {
      const deployedSettings = settings.find((item) => item.is_deployed);

      if (deployedSettings) {
        setActiveSettings(deployedSettings);
      }
    }
  }, [settings]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      if (!configurationSettingsPrompt.is_manual) {
        setSystemPromptActive(false);
        setInputData(configurationSettingsPrompt.input_data);
        setSystemPromptText(null);
      } else {
        setSystemPromptActive(true);
        setInputData({});
        setSystemPromptText(configurationSettingsPrompt.text);
      }
    }
  }, [configurationSettingsPrompt]);

  useEffect(() => {
    if (activeSettings) {
      getConfigurationPrompt({ project_id: id, settings_id: activeSettings?.id });
    }
  }, [activeSettings]);

  return (
    <CardContainer className={clsx('!h-full z-50', isFullScreenConfiguration && 'overflow-hidden')}>
      <div
        className={clsx(
          (!currentProject?.is_active || isInactivePlan) && 'opacity-30 pointer-events-none',
          'card-header z-50',
        )}
      >
        <div className="title cursor-default flex w-full justify-between">
          <div className="flex items-center gap-2">
            <span className="!no-underline max-md:hidden text-sm font-bold text-[#7A6BE5]">
              AI Configuration
            </span>
            <span className="!no-underline max-md:hidden text-tiny font-medium opacity-30">
              {systemPromptActive ? 'System prompt —' : ''}{' '}
              {activeSettings ? `Version ${activeSettings.version}` : ''}
            </span>
          </div>
          <div className="flex gap-2.5">
            <GoToButton href={ROUTE_PATHS.DASHBOARD_PROJECT_CONFIGURATION.replace(':id', id)} />
            {systemPromptActive && (
              <FullScreenButton onClick={fullScreenConfigurationBtnClickHandler} />
            )}
          </div>
        </div>
      </div>
      <div
        className={clsx(
          'card-body flex relative w-full h-[calc(100%-20px)] gap-2.5 max-md:flex-col !mt-[17px] z-50',
          (!currentProject?.is_active || isInactivePlan) && 'opacity-30 pointer-events-none',
        )}
      >
        {(systemPromptText || (activeSettings && Boolean(Object.keys(inputData).length))) && (
          <AiConfigurationFields
            data={inputData}
            text={systemPromptText}
            isFullScreenConfiguration={isFullScreenConfiguration}
          />
        )}
        {!systemPromptText &&
          activeSettings?.is_deployed &&
          Boolean(!Object.keys(inputData).length) && (
            <div className="empty-message w-full">
              <LoaderWrapper
                loader={<Skeleton className="w-full h-11" />}
                isLoading={
                  isLoadingAllConfigurationSettings || isLoadingConfigurationSettingsPrompt
                }
              >
                <p className="opacity-80 font-semibold text-sm text-ebony min-h-11">
                  Custom configuration by CoSupport AI
                </p>
              </LoaderWrapper>
            </div>
          )}
        {!activeSettings?.is_deployed && (
          <div className="empty-message w-full">
            <LoaderWrapper
              loader={<Skeleton className="w-full h-11" />}
              isLoading={isLoadingAllConfigurationSettings || isLoadingConfigurationSettingsPrompt}
            >
              <p className="opacity-70 font-semibold text-base text-ebony min-h-11">
                No Deployed Settings
              </p>
            </LoaderWrapper>
          </div>
        )}
      </div>
    </CardContainer>
  );
};
